import type { ScratchVm } from "./scratch-editor";
import { locale, type Locale } from "./locale";

export const settleProject = (): Promise<void> =>
    new Promise((resolve) => window.setTimeout(resolve, 750));

interface DefaultProjectMessage {
    defaultMessage: string;
    id: string;
}

const DEFAULT_PROJECT_TRANSLATIONS: Record<string, string> = {
    "gui.defaultProject.meow": "Miau",
    "gui.defaultProject.variable": "moja zmienna",
    "gui.sharedMessages.backdrop": "tło{index}",
    "gui.sharedMessages.costume": "kostium{index}",
    "gui.sharedMessages.pop": "pop",
    "gui.sharedMessages.sprite": "Duszek{index}",
};

const SPANISH_DEFAULT_PROJECT_TRANSLATIONS: Record<string, string> = {
    "gui.defaultProject.meow": "Miau",
    "gui.defaultProject.variable": "mi variable",
    "gui.sharedMessages.backdrop": "fondo{index}",
    "gui.sharedMessages.costume": "disfraz{index}",
    "gui.sharedMessages.pop": "pop",
};

export const translatePolishDefaultProject = (
    message: DefaultProjectMessage,
    options?: {index: number},
): string => {
    const translated = DEFAULT_PROJECT_TRANSLATIONS[message.id] ?? message.defaultMessage;
    return translated.replace("{index}", String(options?.index ?? ""));
};

export const translateDefaultProjectForLocale = (
    target: Locale,
    message: DefaultProjectMessage,
    options?: {index: number},
): string => {
    if (target === "pl") return translatePolishDefaultProject(message, options);
    if (message.id === "gui.sharedMessages.sprite") return "Giant";
    const translated = target === "es" ?
        SPANISH_DEFAULT_PROJECT_TRANSLATIONS[message.id] ?? message.defaultMessage :
        message.defaultMessage;
    return translated.replace("{index}", String(options?.index ?? ""));
};

export const translateDefaultProject = (
    message: DefaultProjectMessage,
    options?: {index: number},
): string => translateDefaultProjectForLocale(locale, message, options);

export const loadDefaultProject = async (
    vm: ScratchVm,
    settle: () => Promise<void> = settleProject,
): Promise<void> => {
    const defaultProject = window.GUI.buildDefaultProject(translateDefaultProject)[0];
    if (typeof defaultProject.data !== "string")
        throw new Error("DEFAULT_PROJECT_UNAVAILABLE");
    await vm.loadProject(defaultProject.data);
    await settle();
};

export const createProjectThumbnail = async (
    vm: ScratchVm,
): Promise<Blob | undefined> => {
    const renderer = vm.renderer;
    if (!renderer?.canvas) return undefined;
    vm.postIOData?.("video", { forceTransparentPreview: true });
    try {
        renderer.draw();
        const canvas = document.createElement("canvas");
        canvas.width = 480;
        canvas.height = 360;
        const context = canvas.getContext("2d");
        if (!context) return undefined;
        context.drawImage(renderer.canvas, 0, 0, canvas.width, canvas.height);
        return await new Promise((resolve) =>
            canvas.toBlob(
                (blob) => resolve(blob ?? undefined),
                "image/png",
            ),
        );
    } finally {
        vm.postIOData?.("video", { forceTransparentPreview: false });
    }
};

export const sanitizedProjectFilename = (name: string): string => {
    const sanitized = name
        .normalize("NFKC")
        .split("")
        .map((character) =>
            character.charCodeAt(0) < 32 ? "-" : character,
        )
        .join("")
        .replace(/[\\/:*?"<>|]/gu, "-")
        .replace(/\s+/gu, " ")
        .trim()
        .replace(/[. ]+$/gu, "")
        .slice(0, 100);
    return `${sanitized || "projekt-scratch"}.sb3`;
};

export const downloadProject = async (
    vm: ScratchVm,
    name: string,
): Promise<void> => {
    const blob = await vm.saveProjectSb3();
    const url = URL.createObjectURL(blob);
    try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = sanitizedProjectFilename(name);
        anchor.click();
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
    }
};
